/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable, from, of, forkJoin, throwError, Subject } from 'rxjs';
import { map, concatMap, catchError, last, take, mergeMap, finalize, tap } from 'rxjs/operators';
import { IDBPDatabase, unwrap } from 'idb';
import { set } from 'lodash';
import { PerformanceApiEntryTypes } from '../../crimson';
import { RequestSerializer } from '../../request-serializer/request-serializer';
import {
    DbStoreDefault,
    DbUpgradeMap,
    DbDataStoreConfig,
    DbItemMetadeta,
    DbPerformanceMarks,
    DbPerformanceMeasures
} from './db.models';

export class DbDataStore {
    private readonly versionKey = '_version';
    name: string;
    dbName: string;
    userId: string;
    connected = false;
    private upgrade$: Observable<DbDataStore>;

    constructor(
        private config: DbDataStoreConfig,
        private onDbItemChange: Subject<DbItemMetadeta>,
        private requestQueue: RequestSerializer<IDBPDatabase>
    ) {
        this.name = this.config.storeName;
        this.dbName = this.config.dbName;
        this.userId = this.config.userId;

        if (this.config.storeDefinition) {
            const storeDefinition = this.config.storeDefinition;
            // Upgrade-managed store
            this.upgrade$ = this.upgradeStore(
                this.config.storeName,
                storeDefinition.storeDefault,
                storeDefinition.upgrades,
                storeDefinition.upgradeTargetVersion || 0
            );
        } else {
            this.upgrade$ = of(this);
        }

        this.upgrade$.subscribe(() => {
            this.connected = true;
        });
    }

    private logError(...args: any[]): void {
        console.error('DbDataStore', this.name, JSON.stringify(args));
    }

    private logInfo(...args: any[]): void {
        // eslint-disable-next-line no-console
        console.info('DbDataStore', this.name, JSON.stringify(args));
    }

    private upgradeStore(
        storeName: string,
        storeDefault: DbStoreDefault,
        upgrades: DbUpgradeMap,
        upgradeTargetVersion: number
    ): Observable<DbDataStore> {
        let dbVersion: number;
        return this.getItem(this.versionKey).pipe(
            mergeMap((version: number) => {
                performance.mark(DbPerformanceMarks.UpgradeStart);
                dbVersion = version || 0;
                if (dbVersion === 0 && storeDefault) {
                    dbVersion = storeDefault.version;
                    return this.processDirectUpgrade(storeName, storeDefault, dbVersion);
                } else {
                    return this.processSequentialUpgrades(storeName, dbVersion, upgrades, upgradeTargetVersion);
                }
            }),
            map(() => {
                performance.mark(DbPerformanceMarks.UpgradeEnd);
                const upgradeTime = this.getDbUpgradeDuration();
                this.logInfo(
                    `Time spent in async db upgrade: ${upgradeTime}, store: ${storeName}, new version: ${dbVersion}`
                );
                return this;
            })
        );
    }

    private getDbUpgradeDuration(): number {
        /*
            ToDo: WAR to capture exception for GFN running on Firefox on Android
            Firefox does not return a PerformanceMeasure object when using performance.mark
            and performance.measure, even though MDN says it is supported.

            https://bugzilla.mozilla.org/show_bug.cgi?id=1832543
        */
        try {
            performance.measure(
                DbPerformanceMeasures.Upgrade,
                DbPerformanceMarks.UpgradeStart,
                DbPerformanceMarks.UpgradeEnd
            );
            return performance
                .getEntriesByType(PerformanceApiEntryTypes.Measure)
                .find(entry => entry.name === DbPerformanceMeasures.Upgrade).duration;
        } catch (err) {
            this.logError('Error while calling measure on performance', err);
            return -1;
        }
    }

    private processDirectUpgrade(
        storeName: string,
        storeDefault: DbStoreDefault,
        dbVersion: number
    ): Observable<DbDataStore> {
        const parallelUpgrades: Observable<any>[] = [];
        for (const key of Object.keys(storeDefault.db)) {
            parallelUpgrades.push(this.setItem(key, storeDefault.db[key]));
        }
        return forkJoin(parallelUpgrades).pipe(
            mergeMap(() => {
                this.logInfo(`Store ${storeName} successfully set to default version: ${dbVersion}`);
                return this.setItem(this.versionKey, storeDefault.version);
            }),
            take(1),
            map(() => this),
            catchError(error => {
                this.logError(
                    `Store ${storeName} failed to upgrade version from 0 to ${dbVersion} due to error: ${error}`
                );
                return throwError(() => error);
            })
        );
    }

    private processSequentialUpgrades(
        storeName: string,
        dbVersion: number,
        upgrades: DbUpgradeMap,
        upgradeTargetVersion: number
    ): Observable<DbDataStore> {
        if (dbVersion > upgradeTargetVersion) {
            return throwError(
                () =>
                    `Store ${storeName} db downgrade not supported. Version: ${dbVersion}, Target: ${upgradeTargetVersion}`
            );
        } else if (dbVersion === upgradeTargetVersion) {
            this.logInfo(`Upgrade not required for Store: ${storeName}, Version: ${dbVersion}`);
            return of(this);
        }

        let nextVersion: number;
        return from(upgrades.keys()).pipe(
            concatMap(version => {
                nextVersion = +version;
                if (nextVersion > dbVersion) {
                    this.logInfo(
                        `Store: ${storeName}, attempting upgrade from version: ${dbVersion} to ${nextVersion}`
                    );
                    const nextUpgrade = upgrades.get(nextVersion);
                    return from(nextUpgrade(this)).pipe(
                        mergeMap(() => {
                            this.logInfo(`Store ${storeName} successfully upgraded to version: ${nextVersion}`);
                            dbVersion = nextVersion;
                            return this.setItem(this.versionKey, dbVersion);
                        })
                    );
                }
                return of(this);
            }),
            last(),
            map(() => this),
            catchError(error => {
                this.logError(
                    `Store ${storeName} failed to upgrade db from version: ${dbVersion} to ${nextVersion} due to error ${error}`
                );
                return throwError(() => error);
            })
        );
    }

    getItem(key: string): Observable<any> {
        if (!key) {
            return throwError(() => 'invalid key');
        }
        return this.requestQueue.queue(this.name).pipe(
            mergeMap((idbp: IDBPDatabase) => {
                const tx = idbp.transaction(this.name, 'readonly');
                const store = tx.objectStore(this.name);
                // Prevent unhandled tx.done rejection if the read fails (e.g. corrupted blob)
                // tx.done is not awaited in this read-only flow; only attached to suppress unhandled rejection
                tx.done.catch(err => {
                    this.logError('Read transaction failed for key:', key, err);
                });
                return from(this.preventTransactionCloseOnError(store.get(key)));
            }),
            catchError(error => {
                this.logError('error fetching item', error);
                return throwError(() => error);
            }),
            finalize(() => {
                this.requestQueue.notifyRequestCompletion();
            })
        );
    }

    setItem(key: string, item: any): Observable<any> {
        if (this.config.storeDefinition) {
            if (item instanceof Object) {
                (<any>item).ts = Date.now();
            }
            return this.setItemInDb(key, item).pipe(
                map(_ => {
                    this.onDbItemChange.next({
                        storeName: this.name,
                        userId: this.userId,
                        key
                    });
                    return item;
                })
            );
        }
        return this.setItemInDb(key, item);
    }

    private setItemInDb(key: string, item: any): Observable<any> {
        return this.requestQueue.queue(this.name).pipe(
            mergeMap((idbp: IDBPDatabase) => {
                const tx = idbp.transaction(this.name, 'readwrite');
                const store = tx.objectStore(this.name);
                const request = store.put(item, key);
                return this.finishReadWriteTxObservable(request, tx.done);
            }),
            catchError(error => {
                this.logError('error setting item', error);
                return throwError(() => error);
            }),
            finalize(() => {
                this.requestQueue.notifyRequestCompletion();
            })
        );
    }

    removeItem(key: string): Observable<any> {
        return this.requestQueue.queue(this.name).pipe(
            mergeMap((idbp: IDBPDatabase) => {
                const tx = idbp.transaction(this.name, 'readwrite');
                const store = tx.objectStore(this.name);
                const request = store.delete(key);
                return this.finishReadWriteTxObservable(request, tx.done);
            }),
            catchError(error => {
                this.logError('error removing item', error);
                return throwError(() => error);
            }),
            finalize(() => {
                this.requestQueue.notifyRequestCompletion();
            })
        );
    }

    getAllItems(): Observable<any> {
        return this.requestQueue.queue(this.name).pipe(
            mergeMap((idbp: IDBPDatabase) => {
                const tx = idbp.transaction(this.name, 'readonly');
                const store = tx.objectStore(this.name);
                // Defense-in-depth: prevent unhandled tx.done rejection if a get() error
                // bubbles and aborts the transaction (e.g. when idb patch is not applied)
                tx.done.catch(err => {
                    this.logError('Transaction aborted during getAllItems', err);
                });
                // Note: all get() requests must be dispatched synchronously (within the
                // same microtask as getAllKeys resolution) to keep the transaction alive.
                // Do not add async operators (delay, observeOn, etc.) between these calls.
                return from(store.getAllKeys() as Promise<string[]>).pipe(
                    mergeMap((keys: string[]) => {
                        if (keys.length === 0) {
                            return of({});
                        }
                        // Read each key individually with preventDefault to keep tx alive on error
                        const corruptedKeys: string[] = [];
                        const reads$ = keys.map(key =>
                            from(this.preventTransactionCloseOnError(store.get(key))).pipe(
                                map(value => ({ key, value })),
                                catchError(err => {
                                    this.logError('Skipping corrupted key:', key, err);
                                    corruptedKeys.push(key);
                                    return of(null);
                                })
                            )
                        );
                        return forkJoin(reads$).pipe(
                            map(results => {
                                const result = {};
                                let skipped = 0;
                                for (const entry of results) {
                                    if (entry !== null) {
                                        set(result, entry.key.split('/'), entry.value);
                                    } else {
                                        skipped++;
                                    }
                                }
                                if (skipped > 0) {
                                    this.logInfo(
                                        `Loaded ${keys.length - skipped}/${keys.length} keys (${skipped} skipped)`
                                    );
                                }
                                return result;
                            }),
                            tap(() => {
                                // Self-heal: delete corrupted entries so future reads/writes succeed.
                                // Opens a separate readwrite tx outside the RequestSerializer queue.
                                // Safe because: the readonly tx has completed, IDB serializes readwrite
                                // txs on the same store, and failure is benign — corrupted keys will
                                // simply be skipped again on the next load.
                                if (corruptedKeys.length > 0) {
                                    const deleteTx = idbp.transaction(this.name, 'readwrite');
                                    deleteTx.done.catch(err => {
                                        this.logError('Failed to delete corrupted keys:', corruptedKeys, err);
                                    });
                                    const deleteStore = deleteTx.objectStore(this.name);
                                    corruptedKeys.forEach(k => deleteStore.delete(k));
                                    this.logInfo('Deleting corrupted keys:', corruptedKeys);
                                }
                            })
                        );
                    })
                );
            }),
            catchError(error => {
                this.logError('error fetching items', error);
                return throwError(() => error);
            }),
            finalize(() => {
                this.requestQueue.notifyRequestCompletion();
            })
        );
    }

    clear(): Observable<any> {
        return this.requestQueue.queue(this.name).pipe(
            mergeMap((idbp: IDBPDatabase) => {
                const tx = idbp.transaction(this.name, 'readwrite');
                const store = tx.objectStore(this.name);
                const request = store.clear();
                return this.finishReadWriteTxObservable(request, tx.done);
            }),
            catchError(error => {
                this.logError('error clearing store', error);
                return throwError(() => error);
            }),
            finalize(() => {
                this.requestQueue.notifyRequestCompletion();
            })
        );
    }

    length(): Observable<number> {
        return this.keys().pipe(map((keys: string[]) => keys.length));
    }

    keys(): Observable<string[]> {
        return this.requestQueue.queue(this.name).pipe(
            mergeMap((idbp: IDBPDatabase) => {
                const tx = idbp.transaction(this.name, 'readonly');
                const store = tx.objectStore(this.name);
                return store.getAllKeys() as Promise<string[]>;
            }),
            catchError(error => {
                this.logError('error fetching keys', error);
                return throwError(() => error);
            }),
            finalize(() => {
                this.requestQueue.notifyRequestCompletion();
            })
        );
    }

    private finishReadWriteTxObservable(idbStoreRequest: Promise<any>, idbTxDone: Promise<void>) {
        // To handle cases where the IDBPObjectStore completes successfully but the IDBPTransaction fails,
        // use forkJoin in a similar manner to Promise.all. This allows us to await the completion of both
        // the IDBPObjectStore and IDBPTransaction simultaneously.
        return forkJoin([from(this.preventTransactionCloseOnError(idbStoreRequest)), from(idbTxDone)]);
    }

    private preventTransactionCloseOnError(promise: Promise<any>) {
        // Refer to the relevant error handling issue on idb library Github: https://github.com/jakearchibald/idb/issues/263.
        const request = unwrap(promise);
        request.addEventListener('error', event => {
            // Follows W3C doc to prevent the transaction from aborting.
            // Otherwise the transaction will automatically abort due the failed request.
            // https://www.w3.org/TR/IndexedDB/#introduction
            this.logError('Request failed, error=', request.error);
            event.preventDefault();
            event.stopPropagation();
        });
        return promise;
    }
}
